import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Sparkles, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [logoClickCount, setLogoClickCount] = useState(0);
  const logoClickTimer = useRef(null);
  const navigate = useNavigate();

  const navItems = [
    { name: 'About Us', href: '#about' },
    { name: 'Products', href: '#products' },
    { name: 'Why Choose Us', href: '#why-choose-us' },
    { name: 'Reviews', href: '#testimonials' },
    { name: 'Validation', href: '#validation' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = navItems.map(item => item.href.substring(1));
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  // Secret admin access via logo clicks
  const handleLogoClick = () => {
    setLogoClickCount(prev => prev + 1);
    
    // Clear previous timer
    if (logoClickTimer.current) {
      clearTimeout(logoClickTimer.current);
    }
    
    // Check if 5 clicks reached
    if (logoClickCount + 1 >= 5) {
      navigate('/admin/login');
      setLogoClickCount(0);
      return;
    }
    
    // Reset counter after 2 seconds of inactivity
    logoClickTimer.current = setTimeout(() => {
      setLogoClickCount(0);
    }, 2000);
    
    // Also scroll to top on single click
    scrollToTop();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-blue-100'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-2 group cursor-pointer"
            >
              <div className={`relative p-2 rounded-xl transition-all duration-300 ${
                isScrolled ? 'bg-blue-900' : 'bg-white/90 backdrop-blur-sm'
              }`}>
                <Sparkles className={`w-6 h-6 transition-colors duration-300 ${
                  isScrolled ? 'text-white' : 'text-blue-900'
                }`} />
                <div className="absolute inset-0 bg-blue-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <div className="hidden sm:block">
                <span className={`text-xl font-bold transition-colors duration-300 ${
                  isScrolled ? 'text-blue-900' : 'text-blue-900'
                }`}>
                  <span className="iridescent-text">BIO-PEPTIDE</span>
                </span>
                <span className={`text-sm font-light block transition-colors duration-300 ${
                  isScrolled ? 'text-blue-700' : 'text-blue-800'
                }`}>
                  PEN
                </span>
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item, index) => {
                const isActive = activeSection === item.href.substring(1);
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`relative px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 group ${
                      isActive
                        ? 'text-blue-900 bg-blue-50'
                        : isScrolled
                        ? 'text-gray-700 hover:text-blue-900 hover:bg-blue-50'
                        : 'text-gray-800 hover:text-blue-900 hover:bg-white/80'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <span className="relative z-10">{item.name}</span>
                    
                    {/* Animated underline */}
                    <span className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-gradient-to-r from-sky-400 to-blue-900 transition-all duration-300 ${
                      isActive ? 'w-8' : 'w-0 group-hover:w-8'
                    }`}></span>

                    {/* Hover glow effect */}
                    <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400 to-blue-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></span>
                  </a>
                );
              })}
              
              {/* CTA Button */}
              <a
                href="#validation"
                onClick={(e) => handleNavClick(e, '#validation')}
                className="ml-4 px-6 py-2.5 bg-blue-900 text-white rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2 group relative overflow-hidden"
              >
                <span className="relative z-10">Get Started</span>
                <ChevronRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-xl transition-all duration-300 ${
                isScrolled ? 'bg-blue-50 text-blue-900' : 'bg-white/90 text-blue-900'
              }`}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100">
          <div 
            className="h-full animated-gradient-border transition-all duration-300"
            style={{
              width: `${(window.pageYOffset / (document.documentElement.scrollHeight - window.innerHeight)) * 100}%`
            }}
          ></div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Slide-out */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-80 bg-white z-50 lg:hidden shadow-2xl transform transition-transform duration-500 ease-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-blue-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-900 rounded-xl">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold iridescent-text block">BIO-PEPTIDE</span>
                <span className="text-xs text-blue-700">PEN</span>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 hover:bg-blue-50 rounded-xl transition-colors duration-300"
            >
              <X className="w-6 h-6 text-blue-900" />
            </button>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex-1 overflow-y-auto py-6 px-4">
            <div className="space-y-2">
              {navItems.map((item, index) => {
                const isActive = activeSection === item.href.substring(1);
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`flex items-center justify-between px-4 py-4 rounded-2xl font-medium transition-all duration-300 group ${
                      isActive
                        ? 'bg-blue-900 text-white shadow-lg'
                        : 'bg-blue-50 text-gray-800 hover:bg-blue-100'
                    }`}
                    style={{
                      animation: isMobileMenuOpen ? 'slideIn 0.3s ease-out' : 'none',
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'backwards'
                    }}
                  >
                    <span>{item.name}</span>
                    <ChevronRight className={`w-5 h-5 transition-transform duration-300 ${
                      isActive ? 'text-white' : 'text-blue-600 group-hover:translate-x-1'
                    }`} />
                  </a>
                );
              })}
            </div>

            {/* Mobile CTA */}
            <div className="mt-8">
              <a
                href="#validation"
                onClick={(e) => handleNavClick(e, '#validation')}
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Get Started
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Mobile Menu Footer */}
          <div className="p-6 border-t border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50">
            <p className="text-sm text-gray-600 text-center">
              © 2026 BIO-PEPTIDE Ltd
            </p>
            <p className="text-xs text-gray-500 text-center mt-1">
              Premium Bio-Peptide Solutions
            </p>
          </div>
        </div>
      </div>

      {/* Spacer for fixed navbar */}
      <div className="h-20"></div>
    </>
  );
};

export default Navbar;